import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { 
  ArrowRightIcon,
  DocumentTextIcon,
  CurrencyPoundIcon,
  ClockIcon,
  StarIcon,
  UsersIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  HomeIcon,
  UserGroupIcon,
  HeartIcon,
  AcademicCapIcon,
  ShieldCheckIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { formatCurrency } from '../utils';
import { productService, Product } from '../services/productService';
import { sectorService, Sector } from '../services/sectorService';
import { statsService, Stats } from '../services/statsService';
import { CATEGORY_LABELS, Sector as SectorEnum, SECTOR_LABELS } from '../types';

// Reverse mapping from sector label to enum value
const getSectorEnum = (sectorName: string): SectorEnum | null => {
  const entries = Object.entries(SECTOR_LABELS) as [SectorEnum, string][];
  const found = entries.find(([_, label]) => label === sectorName);
  return found ? found[0] : null;
};

// Icon mapping based on sector type
const getSectorIcon = (sectorName: string) => {
  const name = sectorName.toLowerCase();
  if (name.includes('children') || name.includes('child')) {
    return HomeIcon;
  }
  if (name.includes('supported') || name.includes('accommodation')) {
    return BuildingOfficeIcon;
  }
  if (name.includes('domiciliary') || name.includes('home care')) {
    return HeartIcon;
  }
  if (name.includes('adult residential') || name.includes('nursing')) {
    return BuildingOfficeIcon;
  }
  if (name.includes('fostering')) {
    return UserGroupIcon;
  }
  if (name.includes('day centre') || name.includes('community')) {
    return BuildingOfficeIcon;
  }
  if (name.includes('sen') || name.includes('specialist') || name.includes('education')) {
    return AcademicCapIcon;
  }
  if (name.includes('mental health')) {
    return SparklesIcon;
  }
  return BuildingOfficeIcon; // Default icon
};

export const HomePage: React.FC = () => {
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSectors, setIsLoadingSectors] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  useEffect(() => {
    const loadFeaturedProducts = async () => {
      setIsLoading(true);
      try {
        // Fetch approved products and get the first 6 for featured section
        const products = await productService.getAllProducts({
          status: 'APPROVED',
        });
        
        // Sort by rating and downloads, then take top 6
        const sortedProducts = products
          .filter((p) => p.status === 'APPROVED')
          .sort((a, b) => {
            const ratingA = (a.rating || 0) * 0.6 + (a.downloads || 0) * 0.4;
            const ratingB = (b.rating || 0) * 0.6 + (b.downloads || 0) * 0.4;
            return ratingB - ratingA;
          })
          .slice(0, 6);
        
        setFeaturedProducts(sortedProducts);
      } catch (error) {
        console.error('Error loading featured products:', error);
        // Keep empty array on error
        setFeaturedProducts([]);
      } finally {
        setIsLoading(false);
      }
    };

    const loadSectors = async () => {
      setIsLoadingSectors(true);
      try {
        const response = await sectorService.getAllSectors();
        setSectors(response.sectors || []);
      } catch (error) {
        console.error('Error loading sectorsss:', error);
        setSectors([]);
      } finally {
        setIsLoadingSectors(false);
      }
    };

    const loadStats = async () => {
      setIsLoadingStats(true);
      try {
        const statsData = await statsService.getStats();
        setStats(statsData);
      } catch (error) {
        console.error('Error loading stats:', error);
        // Keep null on error, will show default values
        setStats(null);
      } finally {
        setIsLoadingStats(false);
      }
    };

    loadFeaturedProducts();
    loadSectors();
    loadStats();
  }, []);

  const formatStatValue = (value: number, suffix: string = ''): string => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}k${suffix}`;
    }
    return `${value}${suffix}`;
  };

  const statsData = [
    { 
      label: 'Active Sellers', 
      value: stats ? formatStatValue(stats.activeSellers, '+') : '150+', 
      icon: UsersIcon 
    },
    { 
      label: 'Products Sold', 
      value: stats ? formatStatValue(stats.productsSold, '+') : '2,500+', 
      icon: DocumentTextIcon 
    },
    { 
      label: 'Happy Customers', 
      value: stats ? formatStatValue(stats.happyCustomers, '+') : '1,200+', 
      icon: StarIcon 
    },
    { 
      label: 'Avg. Rating', 
      value: stats ? `${stats.avgRating}/5` : '4.8/5', 
      icon: ChartBarIcon 
    },
  ];


  return (
    <div className="bg-white">
      {/* Test change detection - updated on 2025-02-05 */}
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="text-center animate-fadeIn">
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 leading-tight">
              Buy and sell compliant care documents in one place
            </h1>
            <p className="font-body text-lg md:text-xl lg:text-2xl text-primary-50 mb-10 max-w-3xl mx-auto leading-relaxed font-light">
              Thrive Care connects UK care professionals with quality templates, 
              policies, and resources. Save time, ensure compliance, and grow your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slideInUp" style={{ animationDelay: '0.2s' }}>
              <Link to="/marketplace">
                <Button size="lg" className="bg-white text-primary-700 hover:bg-primary-50 shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5 transition-all duration-300 font-semibold px-8 py-4">
                  Browse Marketplace
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/sell">
                <Button variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white hover:text-primary-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 font-semibold px-8 py-4">
                  Start Selling
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isLoadingStats ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                <p className="mt-4 text-secondary-600 font-body">Loading statistics...</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {statsData.map((stat, index) => (
                <div 
                  key={stat.label} 
                  className="text-center group animate-fadeIn"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flex justify-center mb-4">
                    <div className="p-4 bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl group-hover:from-primary-100 group-hover:to-primary-200 transition-all duration-300 transform group-hover:scale-110 shadow-sm group-hover:shadow-md">
                      <stat.icon className="h-6 w-6 text-primary-600" />
                    </div>
                  </div>
                  <div className="text-3xl md:text-4xl font-display font-bold text-secondary-900 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm md:text-base text-secondary-600 font-body">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16 animate-fadeIn">
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-secondary-900 mb-4">
              Featured Products
            </h2>
            <p className="font-body text-lg md:text-xl text-secondary-600 max-w-2xl mx-auto leading-relaxed">
              Discover our most popular and highly-rated care documents from trusted sellers
            </p>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                <p className="mt-4 text-secondary-600">Loading featured products...</p>
              </div>
            </div>
          ) : featuredProducts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProducts.map((product) => {
                // Get image URL - prefer signed URL from backend
                const getImageUrl = (): string => {
                  if (product.thumbnailUrl) return product.thumbnailUrl;
                  const thumbnailMedia = product.media?.find(m => m.entityType === 'PRODUCT_THUMBNAIL');
                  if (thumbnailMedia?.signedUrl) return thumbnailMedia.signedUrl;
                  if (thumbnailMedia?.fileUrl) return thumbnailMedia.fileUrl;
                  if (product.thumbnail) {
                    return `https://markteplacemedia.s3.eu-north-1.amazonaws.com/${product.thumbnail}`;
                  }
                  return 'https://via.placeholder.com/400x300?text=No+Image';
                };

                const reviewCount = product._count?.reviews || product.reviewCount || 0;
                const rating = product.rating || 0;

                return (
                  <Link 
                    key={product.id} 
                    to={`/product/${product.id}`}
                    className="animate-fadeIn block"
                  >
                    <Card hover className="group h-full transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 border border-secondary-100 hover:border-primary-200">
                      <div className="aspect-w-16 aspect-h-9 mb-4 overflow-hidden rounded-lg">
                        <img
                          src={getImageUrl()}
                          alt={product.title}
                          className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x300?text=No+Image';
                          }}
                        />
                      </div>
                      <div className="space-y-3 p-1">
                        <div className="flex items-start justify-between">
                          <Badge variant="primary" size="sm" className="font-body">
                            {(CATEGORY_LABELS as Record<string, string>)[product.category] || product.category.replace('_', ' ')}
                          </Badge>
                          <div className="flex items-center text-sm text-secondary-600 font-body">
                            <StarIcon className="h-4 w-4 text-yellow-400 mr-1 fill-current" />
                            {rating.toFixed(1)} ({reviewCount})
                          </div>
                        </div>
                        
                        <h3 className="font-subheading text-lg font-semibold text-secondary-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {product.title}
                        </h3>
                        
                        <p className="text-sm text-secondary-600 line-clamp-2 font-body leading-relaxed">
                          {product.description}
                        </p>
                        
                        <div className="flex items-center justify-between pt-2 border-t border-secondary-100">
                          <div className="text-xl font-display font-bold text-primary-600">
                            {formatCurrency(parseFloat(product.price))}
                          </div>
                          <div className="flex items-center text-xs text-secondary-500 font-body">
                            <ClockIcon className="h-3.5 w-3.5 mr-1" />
                            {product.downloads || 0} downloads
                          </div>
                        </div>
                      </div>
                    </Card>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-secondary-600">
                No featured products available at the moment. Check back soon!
              </p>
            </div>
          )}

          <div className="text-center mt-12 md:mt-16">
            <Link to="/marketplace">
              <Button variant="outline" size="lg" className="font-semibold px-8 py-4 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-300">
                View All Products
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Sectors Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-secondary-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16 animate-fadeIn">
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-secondary-900 mb-4">
              Serving All Care Sectors
            </h2>
            <p className="font-body text-lg md:text-xl text-secondary-600 max-w-2xl mx-auto leading-relaxed">
              From children's homes to mental health services, we support every area of UK care
            </p>
          </div>

          {isLoadingSectors ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                <p className="mt-4 text-secondary-600">Loading sectors...</p>
              </div>
            </div>
          ) : sectors.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sectors.map((sector) => {
                const sectorEnum = getSectorEnum(sector.name);
                const sectorParam = sectorEnum || sector.name;
                const IconComponent = getSectorIcon(sector.name);
                
                return (
                  <Link
                    key={sector.id}
                    to={`/marketplace?sector=${encodeURIComponent(sectorParam)}`}
                    className="block group"
                  >
                    <Card hover className="h-full transition-all duration-300 hover:shadow-lg hover:-translate-y-1 border-2 border-transparent hover:border-primary-200">
                      <div className="p-6 h-full flex flex-col">
                        {/* Icon and Regulator Badge */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="p-3 bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl group-hover:from-primary-100 group-hover:to-primary-200 transition-all duration-300">
                            <IconComponent className="h-7 w-7 text-primary-600 group-hover:scale-110 transition-transform duration-300" />
                          </div>
                          {sector.regulator && (
                            <Badge variant="primary" size="sm" className="flex items-center gap-1">
                              <ShieldCheckIcon className="h-3 w-3" />
                              {sector.regulator}
                            </Badge>
                          )}
                        </div>
                        
                        {/* Sector Name */}
                        <h3 className="font-subheading text-lg md:text-xl font-bold text-secondary-900 mb-2 group-hover:text-primary-600 transition-colors duration-300">
                          {sector.name}
                        </h3>
                        
                        {/* Product Count */}
                        <div className="mt-auto pt-4 border-t border-secondary-100">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-secondary-600 font-body">
                              {sector.productCount || 0} {sector.productCount === 1 ? 'product' : 'products'} available
                            </span>
                            <div className="flex items-center text-primary-600 group-hover:text-primary-700 font-semibold text-sm transition-colors duration-300 font-body">
                              <span className="mr-1">Explore</span>
                              <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-secondary-600">
                No sectors available at the moment.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 md:py-24 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl -translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center animate-fadeIn">
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            Ready to get started?
          </h2>
          <p className="font-body text-lg md:text-xl lg:text-2xl text-primary-50 mb-10 max-w-2xl mx-auto leading-relaxed">
            Join thousands of care professionals who trust Thrive Care for their compliance needs
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slideInUp">
            <Link to="/marketplace">
              <Button size="lg" className="bg-white text-primary-700 hover:bg-primary-50 shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5 transition-all duration-300 font-semibold px-8 py-4">
                <DocumentTextIcon className="mr-2 h-5 w-5" />
                Browse Documents
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white hover:text-primary-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 font-semibold px-8 py-4">
                <CurrencyPoundIcon className="mr-2 h-5 w-5" />
                Start Selling
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
